import { Images } from "lucide-react"
import { useRef, useState } from "react"
import { LibraryPickerDialog } from "@/components/library/library-picker-dialog"
import type { LibraryItem } from "@/components/library/library-item"
import { AddDashedTile } from "@/components/ui/add-dashed-tile"
import { libraryPickResult, type LibraryPickResult } from "@/hooks/use-library-pick-selection"
import { useTranslation } from "@/hooks/use-translation"
import {
  type FlowAttachMedia,
  type FlowAttachOutcome,
  type FlowAttachPick,
  type FlowAttachRequest,
} from "@/lib/flow-attach-input"
import type { LibraryPickMedia } from "@/lib/library-source"
import type { ShotFrameSource } from "@/lib/workspace-api"

type FlowReferenceAdderProps = {
  projectId: number
  frameFormat: string
  frameRate?: number
  initialShotId?: number | null
  accepts: readonly LibraryPickMedia[]
  fileAccept: string
  multiple?: boolean
  selectionLimit?: number
  disabled?: boolean
  onAttach: (batch: FlowAttachRequest) => Promise<FlowAttachOutcome>
}

/** Выбор из библиотеки → пики для attach. `only` — оставить один тип медиа (слот цепочки). */
export function flowAttachPicks(picked: readonly LibraryPickResult[], only?: FlowAttachMedia): FlowAttachPick[] {
  return picked.flatMap((entry) => {
    if (!attachMediaMatches(entry.media, only)) return []
    return [{ source: entry.source, media: entry.media, label: entry.label }]
  })
}

function attachMediaMatches(media: LibraryPickMedia, only: FlowAttachMedia | undefined): media is FlowAttachMedia {
  if (only) return media === only
  return media === "image" || media === "video" || media === "audio"
}

export function FlowReferenceAdder({
  projectId,
  frameFormat,
  frameRate,
  initialShotId,
  accepts,
  fileAccept,
  multiple = true,
  selectionLimit,
  disabled = false,
  onAttach,
}: FlowReferenceAdderProps) {
  const { t } = useTranslation(["common", "shot"])
  const fileRef = useRef<HTMLInputElement>(null)
  const busyRef = useRef(false)
  const [libraryOpen, setLibraryOpen] = useState(false)
  const [busy, setBusy] = useState<"file" | "library" | null>(null)
  const locked = disabled || busy != null

  const run = async (which: "file" | "library", batch: FlowAttachRequest) => {
    if (busyRef.current) return null
    busyRef.current = true
    setBusy(which)
    try {
      return await onAttach(batch)
    } finally {
      busyRef.current = false
      setBusy(null)
    }
  }

  const finishLibrary = (source: ShotFrameSource, item: LibraryItem) => {
    const picked = libraryPickResult(item, source)
    void run("library", { picks: flowAttachPicks([picked]) }).then((outcome) => {
      if (outcome && outcome.failed === 0 && outcome.attached > 0) setLibraryOpen(false)
    })
  }

  return (
    <>
      <AddDashedTile
        onClick={() => fileRef.current?.click()}
        uploading={busy === "file"}
        disabled={locked}
        ariaLabel={t("referenceInputs.addReference")}
      />
      <AddDashedTile
        onClick={() => setLibraryOpen(true)}
        uploading={busy === "library"}
        disabled={locked}
        ariaLabel={t("referenceInputs.addFromLibrary", { ns: "shot" })}
        title={t("referenceInputs.addFromLibrary", { ns: "shot" })}
        icon={<Images className="h-5 w-5" aria-hidden />}
      />
      <input
        ref={fileRef}
        type="file"
        accept={fileAccept}
        multiple={multiple}
        className="hidden"
        onChange={(event) => {
          const files = Array.from(event.target.files ?? [])
          event.target.value = ""
          if (files.length === 0) return
          void run("file", { files })
        }}
      />
      <LibraryPickerDialog
        open={libraryOpen}
        onOpenChange={setLibraryOpen}
        projectId={projectId}
        frameFormat={frameFormat}
        frameRate={frameRate}
        initialShotId={initialShotId}
        multiple={multiple}
        selectionLimit={selectionLimit}
        subtitle={t("referenceInputs.addFromLibrary", { ns: "shot" })}
        targets={[
          {
            id: "attach",
            label: t("referenceInputs.attach", { ns: "shot" }),
            accepts,
          },
        ]}
        defaultTargetId="attach"
        busyTargetId={busy === "library" ? "attach" : null}
        onPick={(_targetId, source, item) => finishLibrary(source, item)}
        onPickMany={(_targetId, picked) => {
          void run("library", { picks: flowAttachPicks(picked) }).then((outcome) => {
            if (outcome && outcome.failed === 0 && outcome.attached > 0) setLibraryOpen(false)
          })
        }}
      />
    </>
  )
}
